import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlugsIcon, ArrowsClockwiseIcon, WarningIcon, BuildingsIcon } from "@phosphor-icons/react";

export default function Channels() {
  const { properties, activeId, setActive } = useProperty();
  const [channels, setChannels] = useState([]);
  const [busy, setBusy] = useState(null);

  const propId = activeId;
  const activeProp = properties.find((p) => p.id === propId);

  const load = () => {
    if (!propId) return;
    api.get("/channels", { params: { property_id: propId } }).then((r) => setChannels(r.data));
  };
  useEffect(() => { load(); }, [propId]);

  const toggleConnect = async (ch) => {
    setBusy(ch.id + ":c");
    try {
      await api.put(`/channels/${ch.id}`, { property_id: propId, connected: !ch.connected });
      toast.success(`${ch.connected ? "Disconnected" : "Connected"} ${ch.name} for ${activeProp?.name || "this property"} (mock)`);
      load();
    } finally { setBusy(null); }
  };
  const toggleSync = async (ch) => {
    setBusy(ch.id + ":s");
    try {
      await api.put(`/channels/${ch.id}`, { property_id: propId, sync_enabled: !ch.sync_enabled });
      load();
    } finally { setBusy(null); }
  };
  const syncNow = async (ch) => {
    setBusy(ch.id + ":n");
    try {
      await api.post(`/channels/${ch.id}/sync`, null, { params: { property_id: propId } });
      toast.success(`${ch.name} inventory pushed for ${activeProp?.name || "this property"} (mock)`);
      load();
    } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader
        overline="Distribution"
        title="Channel Manager"
        description="Push room availability to OTAs, per property. Connections below are MOCKED — no real OTA calls are made without live credentials."
      />

      <Card className="p-4 mb-6 flex flex-wrap items-center gap-4">
        <BuildingsIcon size={20} className="text-primary" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Configuring channels for</p>
          <Select value={propId || ""} onValueChange={setActive}>
            <SelectTrigger className="mt-1 w-full max-w-sm" data-testid="channels-property-select"><SelectValue placeholder="Pick property" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground max-w-md">Each hotel has its own OTA connection state. Toggling here only affects the selected property.</p>
      </Card>

      <Card className="p-4 mb-6 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 flex gap-3 items-start">
        <WarningIcon size={20} className="text-[hsl(var(--warning))] mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Mock channel connections</p>
          <p className="text-muted-foreground">Toggles and sync are simulated per hotel. To go live, provide OTA API credentials for each property and we'll wire real inventory sync.</p>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {channels.map((ch) => (
          <Card key={ch.id} className="p-6 flex flex-col" data-testid={`channel-card-${ch.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-md flex items-center justify-center font-display font-bold text-white" style={{ background: ch.color }}>
                {ch.logo}
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg">{ch.name}</h3>
                <p className="text-xs text-muted-foreground">Commission {ch.commission_pct}%</p>
              </div>
              {ch.connected ? (
                <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">Connected</Badge>
              ) : (
                <Badge variant="outline" className="bg-muted text-muted-foreground">Idle</Badge>
              )}
            </div>
            <div className="mt-6 space-y-4 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Connection</p>
                  <p className="text-xs text-muted-foreground">Enable to push inventory</p>
                </div>
                <Switch checked={ch.connected} onCheckedChange={() => toggleConnect(ch)} disabled={busy === ch.id + ":c"} data-testid={`channel-connect-${ch.id}`} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Auto sync</p>
                  <p className="text-xs text-muted-foreground">Real-time availability + rates</p>
                </div>
                <Switch checked={ch.sync_enabled} onCheckedChange={() => toggleSync(ch)} disabled={!ch.connected || busy === ch.id + ":s"} data-testid={`channel-sync-${ch.id}`} />
              </div>
              {ch.last_sync && (
                <p className="text-xs text-muted-foreground">Last sync: {new Date(ch.last_sync).toLocaleString("en-IN")}</p>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => syncNow(ch)} disabled={!ch.connected || busy === ch.id + ":n"} data-testid={`channel-sync-now-${ch.id}`}>
                <ArrowsClockwiseIcon size={14} className="mr-1" /> Sync now
              </Button>
              <Button size="sm" variant="ghost" className="flex-1" disabled>
                <PlugsIcon size={14} className="mr-1" /> Settings
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
